import { CustomAppSecretModal } from "@/modules/admin/custom-apps/components/panels/CustomAppSecretModal";
import ApiTokenModal from "@/modules/identity-and-access/api-tokens/components/ApiTokenModal";
import { queryClient } from "@/queryClient";
import { useDialog, useToast } from "@vintecc/components";
import { AxiosResponse, isAxiosError } from "axios";

type DotnetErrorPayload = {
  message: string;
  code?: string | number;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isErrorWithMessage = (value: unknown): value is { message: string } => isRecord(value) && typeof value.message === "string";

const isDotnetErrorPayload = (value: unknown): value is DotnetErrorPayload => {
  if (!isErrorWithMessage(value)) {
    return false;
  }

  return !("code" in value) || typeof value.code === "string" || typeof value.code === "number";
};

const getAxiosPayloadFallbackMessage = (payload: unknown, defaultMessage: string): string => {
  if (typeof payload === "string") {
    return payload;
  }

  if (payload == null) {
    return defaultMessage;
  }

  if (isRecord(payload) || Array.isArray(payload)) {
    return JSON.stringify(payload) || defaultMessage;
  }

  return String(payload);
};

export const useApiToast = (id: string | string[]) => {
  const { openToast } = useToast();
  const apiTokenModal = useDialog(ApiTokenModal);
  const customAppSecretModal = useDialog(CustomAppSecretModal);

  return {
    onSuccess: (response: AxiosResponse) => {
      if (typeof response.data === "string") {
        openToast({
          message: response.data,
          intent: "success",
        });
      } else if (response.data?.message && typeof response.data.message === "string") {
        openToast({
          message: response.data.message,
          intent: "success",
        });
      }

      queryClient.invalidateQueries({ queryKey: Array.isArray(id) ? id : [id] });
    },
    onSuccessNoInvalidation: (response: AxiosResponse) => {
      if (typeof response.data === "string") {
        openToast({
          message: response.data,
          intent: "success",
        });
      } else if (response.data?.message && typeof response.data.message === "string") {
        openToast({
          message: response.data.message,
          intent: "success",
        });
      }
    },
    onSuccessApiTokenCreation: (response: AxiosResponse) => {
      if (typeof response.data === "string") {
        apiTokenModal.open({
          token: response.data,
        });
      }
      queryClient.invalidateQueries({ queryKey: Array.isArray(id) ? id : [id] });
    },
    onSuccessCustomAppCreation: (response: AxiosResponse) => {
      if (response.data) {
        customAppSecretModal.open({
          secret: response.data.oAuthClientSecret,
        });
      }
      queryClient.invalidateQueries({ queryKey: Array.isArray(id) ? id : [id] });
    },
    onError: (e: unknown) => {
      if (isAxiosError(e)) {
        const dotnetError = e.response?.data;
        if (isDotnetErrorPayload(dotnetError)) {
          openToast({
            message: `${dotnetError.code ? `[${dotnetError.code}] ` : ""}${dotnetError.message}`,
            intent: "error",
          });
        } else {
          openToast({
            message: getAxiosPayloadFallbackMessage(dotnetError, e.message),
            intent: "error",
          });
        }
        // Handle v1 API error format
      } else if (isErrorWithMessage(e)) {
        openToast({
          message: e.message,
          intent: "error",
        });
      } else {
        openToast({
          message: String(e),
          intent: "error",
        });
      }
    },
  };
};
